import { useRef, useState } from "react";
import {
  Check,
  ChevronLeft,
  ChevronRight,
  Copy,
  Ellipsis,
  PlusSquare,
  Share,
} from "lucide-react";
import { config } from "../config";
import { t } from "../locale";
import InstallReminder from "./InstallReminder";

export function getInstallContext() {
  const ua = navigator.userAgent;
  const ios =
    /iPhone|iPad|iPod/.test(ua) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const embedded =
    /MicroMessenger|FBAN|FBAV|Instagram|\bLine\/|\bQQ\/|TikTok/i.test(ua);
  const safari = /Safari\//.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
  return { ios, handoff: !ios || embedded || !safari };
}

export const isHomeScreen = () =>
  matchMedia("(display-mode: standalone)").matches ||
  Boolean((navigator as Navigator & { standalone?: boolean }).standalone);

function CopyInstallLink() {
  const [copyState, setCopyState] = useState<"idle" | "copied" | "manual">(
    "idle",
  );
  const address = useRef<HTMLInputElement>(null);
  // Always copy our homepage, not a player, recovery URL, or tracking token.
  const url = new URL("/", location.origin).href;
  async function copyLink() {
    try {
      await navigator.clipboard.writeText(url);
      setCopyState("copied");
    } catch {
      setCopyState("manual");
      address.current?.focus();
      address.current?.select();
      address.current?.setSelectionRange(0, url.length);
    }
  }
  return (
    <>
      <div className="install-copy-row">
        <input
          ref={address}
          value={url}
          readOnly
          aria-label={t("Website address")}
          onFocus={(event) => event.currentTarget.select()}
        />
        <button className="secondary-button" onClick={() => void copyLink()}>
          {copyState === "copied" ? <Check size={16} /> : <Copy size={16} />}
          {copyState === "copied" ? t("Link copied") : t("Copy link")}
        </button>
      </div>
      <p role="status">
        {copyState === "copied"
          ? t(
              "Open Safari, paste the link into the address bar, then visit the page.",
            )
          : copyState === "manual"
            ? t("Press and hold the selected address to copy it.")
            : ""}
      </p>
    </>
  );
}

export default function InstallGuide({ onClose }: { onClose: () => void }) {
  const { ios, handoff } = getInstallContext();
  // Keep the preview name identical to the icon's configured iOS title.
  const appName = config.locale === "zh-CN" ? "Yestv 测试版" : "Yestv";
  const steps = (
    <ol className="home-install-steps apple-install-steps">
      <li>
        <span className="home-step-number" aria-hidden="true">
          1
        </span>
        <div>
          <strong>{t("Tap Safari’s Share button")}</strong>
          <p>
            {t(
              "Look for the square with an upward arrow. You may need to open the page menu first.",
            )}
          </p>
          <div className="install-sketch" aria-hidden="true">
            <span className="install-sketch-label">{t("Illustration")}</span>
            <div className="safari-toolbar-sketch">
              <ChevronLeft size={19} />
              <span>{location.hostname}</span>
              <span className="sketch-highlight">
                <Share size={23} />
              </span>
              <Ellipsis size={20} />
            </div>
          </div>
        </div>
      </li>
      <li>
        <span className="home-step-number" aria-hidden="true">
          2
        </span>
        <div>
          <strong>{t("Choose Add to Home Screen")}</strong>
          <p>{t("Scroll down the share menu to find this option.")}</p>
          <div className="install-sketch" aria-hidden="true">
            <span className="install-sketch-label">{t("Illustration")}</span>
            <div className="share-menu-sketch">
              <PlusSquare size={19} />
              <span>{t("Add to Home Screen")}</span>
              <ChevronRight size={16} />
            </div>
          </div>
        </div>
      </li>
      <li>
        <span className="home-step-number" aria-hidden="true">
          3
        </span>
        <div>
          <strong>{t("Confirm with Add")}</strong>
          <p>
            {t(
              "The icon and name are ready. Keep Open as Web App on if shown, then tap Add.",
            )}
          </p>
          <div className="install-sketch" aria-hidden="true">
            <span className="install-sketch-label">{t("Illustration")}</span>
            <div className="confirm-sketch-heading">
              <span>{t("Add to Home Screen")}</span>
              <span className="sketch-highlight">{t("Add")}</span>
            </div>
            <div className="confirm-sketch-app">
              <img src="/icons/apple-touch-icon.png" alt="" />
              <span>{appName}</span>
            </div>
            <div className="confirm-sketch-toggle">
              <span>{t("Open as Web App")}</span>
              <span className="sketch-toggle">
                <i />
              </span>
            </div>
          </div>
        </div>
      </li>
    </ol>
  );

  return (
    <>
      <h2 id="portal-dialog-title" className="install-guide-title">
        {t("Add Yestv to your desktop")}
      </h2>
      <div className="home-icon-preview">
        <img src="/icons/apple-touch-icon.png" alt={t("Yestv app icon")} />
        <div>
          <strong>{appName}</strong>
          <p>{t("Your Home Screen icon")}</p>
        </div>
      </div>
      <InstallReminder onClose={onClose} />
      {handoff ? (
        <>
          <div className="install-handoff">
            <strong>
              {ios
                ? t("First, open this page in Safari")
                : t("Open this page in Safari on your iPhone or iPad")}
            </strong>
            <p>
              {ios
                ? t(
                    "Copy this link, switch to Safari, and paste it into the address bar.",
                  )
                : t(
                    "Send the link below to your iPhone or iPad, then open it in Safari.",
                  )}
            </p>
            <CopyInstallLink />
            <p>
              {t(
                "Then tap Add to desktop on that page to see the three steps.",
              )}
            </p>
          </div>
          <details className="install-help install-safari-preview">
            <summary>
              {ios
                ? t("Already in Safari? View the steps")
                : t("Preview the iPhone steps")}
            </summary>
            {steps}
          </details>
        </>
      ) : (
        <>
          <p className="install-guide-intro">
            {t(
              "Follow these three steps in Safari. There is nothing to download or fill in.",
            )}
          </p>
          {steps}
          <details className="install-help">
            <summary>{t("Can’t find Add to Home Screen?")}</summary>
            <p>
              {t(
                "Scroll to the bottom of Safari’s share menu, tap Edit Actions, then add Add to Home Screen.",
              )}
            </p>
            <p>
              {t(
                "If it is still missing, copy this link and open it in the Safari app.",
              )}
            </p>
            <CopyInstallLink />
          </details>
        </>
      )}
      <p className="install-after-note">
        {t(
          "After adding, return to your Home Screen and tap the Yestv icon to open it.",
        )}
      </p>
      <button className="primary-button full-width" onClick={onClose}>
        {t("Close guide")}
      </button>
    </>
  );
}
